import { FC, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import AddCustomPageComponent from 'modules/benefitsWellness/components/AddCustomPageComponent/AddCustomPageComponent';
import EditCustomPageTitle from 'modules/benefitsWellness/components/EditCustomPageTitle/EditCustomPageTitle';
import iconEdit from 'images/icon-edit.svg';

import styles from './customPagesNavigation.module.less';

interface CustomPage {
  id: string;
  title: string;
  path: string;
  components: any[];
}

interface Props {
  customPages: CustomPage[];
  onCreatePage: (
    pageTitle: string,
    selectedComponents: any[]
  ) => Promise<CustomPage>;
  onCheckTitleExists: (title: string) => Promise<boolean>;
  onEditPage?: (pageId: string, newTitle: string) => Promise<void>;
  isEditMode?: boolean;
  onCloseSider?: () => void;
}

const CustomPagesNavigation: FC<Props> = ({
  customPages,
  onCreatePage,
  onCheckTitleExists,
  onEditPage,
  isEditMode = false,
  onCloseSider,
}) => {
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [showEditPageModal, setShowEditPageModal] = useState(false);
  const [editingPage, setEditingPage] = useState<CustomPage | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { hash = '', employerName = '' } = useParams();

  // Extract previewToken from URL search parameters
  const search = location.search;
  const previewToken = new URLSearchParams(search).get('previewToken');

  // Helper function to get the full path for a custom page
  const getCustomPagePath = (pageId: string) => {
    return `/${hash}${
      employerName ? '/' + employerName : ''
    }/custom-page/${pageId}${
      previewToken ? `?previewToken=${previewToken}` : ''
    }`;
  };

  // Helper function to check if a page is currently selected
  const isPageSelected = (pageId: string) => {
    const pagePath = `/${hash}${
      employerName ? '/' + employerName : ''
    }/custom-page/${pageId}`;
    return location.pathname === pagePath;
  };

  const handlePageClick = (page: CustomPage) => {
    const path = getCustomPagePath(page.id);
    navigate(path);
    onCloseSider?.();
  };

  const handleCreatePage = async (
    pageTitle: string,
    selectedComponents: any[]
  ) => {
    try {
      console.log('🔧 CustomPagesNavigation.handleCreatePage called with:', {
        pageTitle,
        selectedComponents,
      });

      const newPage = await onCreatePage(pageTitle, selectedComponents);
      setShowAddPageModal(false);

      // Navigate to the newly created page
      if (newPage) {
        const path = getCustomPagePath(newPage.id);
        navigate(path);
        onCloseSider?.();
      }
    } catch (error) {
      console.error('Error creating page:', error);
    }
  };

  const handleEditPageClick = (page: CustomPage, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingPage(page);
    setShowEditPageModal(true);
  };

  const handleEditPageSave = async (newTitle: string) => {
    if (editingPage && onEditPage) {
      try {
        await onEditPage(editingPage.id, newTitle);
        setShowEditPageModal(false);
        setEditingPage(null);
      } catch (error) {
        console.error('Error editing page:', error);
      }
    }
  };

  const handleEditPageClose = () => {
    setShowEditPageModal(false);
    setEditingPage(null);
  };

  return (
    <>
      {/* Custom Pages Section Title - Non-clickable */}
      {isEditMode && (
        <Menu.Item
          key="custom-pages-title"
          className={styles.customPagesTitleItem}
          disabled
        >
          <div className={styles.customPagesTitle}>Custom Pages</div>
        </Menu.Item>
      )}

      {/* List of Created Custom Pages */}
      {customPages.map((page) => (
        <Menu.Item
          style={{ padding: 2, textOverflow: 'ellipsis' }}
          key={getCustomPagePath(page.id)}
          className={styles.customPageItem}
          onClick={() => handlePageClick(page)}
        >
          <div
            className={`${styles.customPageItemWrapper} ${
              isEditMode ? styles.editMode : ''
            }`}
          >
            {isEditMode && (
              <img
                className={styles.editIcon}
                src={iconEdit}
                alt="Edit"
                onClick={(e) => handleEditPageClick(page, e)}
              />
            )}
            <div
              className={`${styles.customPageLabel} ${
                isPageSelected(page.id) ? styles.selectedPageLabel : ''
              }`}
            >
              {page.title}
            </div>
          </div>
        </Menu.Item>
      ))}

      {/* Add New Page Button */}
      {isEditMode && (
        <div className={styles.addNewPageWrapper}>
          <button
            className={styles.addNewPageButton}
            type="button"
            onClick={() => setShowAddPageModal(true)}
          >
            <PlusOutlined className={styles.plusIcon} />
            <span className={styles.addNewPageText}>Add New Page</span>
          </button>
        </div>
      )}

      <AddCustomPageComponent
        visible={showAddPageModal}
        onClose={() => setShowAddPageModal(false)}
        onSave={handleCreatePage}
        onCheckTitleExists={onCheckTitleExists}
        textLimit={22}
        isAddingComponent={false}
      />

      <EditCustomPageTitle
        visible={showEditPageModal}
        onClose={handleEditPageClose}
        onSave={handleEditPageSave}
        onCheckTitleExists={onCheckTitleExists}
        initialTitle={editingPage?.title || ''}
        textLimit={22}
      />
    </>
  );
};

CustomPagesNavigation.propTypes = {
  customPages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      components: PropTypes.array.isRequired,
    }).isRequired
  ).isRequired,
  onCreatePage: PropTypes.func.isRequired,
  onCheckTitleExists: PropTypes.func.isRequired,
  onEditPage: PropTypes.func,
  isEditMode: PropTypes.bool,
  onCloseSider: PropTypes.func,
};

CustomPagesNavigation.defaultProps = {
  onEditPage: undefined,
  isEditMode: false,
  onCloseSider: undefined,
};

export default CustomPagesNavigation;
